import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { GlobalAccount } from '@ndvr/customer-api';
import {
    Account,
    AccountAllocationRequest,
    AccountConvictionsSaveRequest,
    AccountGroup,
    AccountGroupAdvisorUpdateRequest,
    AccountGroupAllocationRequest,
    AccountGroupTaxLossHarvestingUpdateRequest,
    AssetLocationPolicy,
    CashFlowConfig,
    CashFlowConfigSaveRequest,
    CashFlowType,
    GUID,
    LifecycleStatus,
    TradeRestrictionAssetRequest,
    TradeRestrictionCategoryOverrideRequest,
    TradeRestrictionCategoryRequest,
} from '@ndvr/portfolio-api';

import { PortfolioApi } from 'API';
import { sortAccountGroupProfiles } from 'utils';
import { Toast } from 'widgets';

export const useAccountGroup = (accountGroupGUID?: GUID<AccountGroup>) => {
    return useQuery({
        queryKey: ['accountGroup', accountGroupGUID],
        queryFn: async () => {
            const [response] = await PortfolioApi().getAccountGroup(accountGroupGUID!);
            return response ?? undefined;
        },
        enabled: !!accountGroupGUID,
    });
};

export const useListAccountGroupProfiles = (householdGUID?: GUID<GlobalAccount>) => {
    return useQuery({
        queryKey: ['globalAccount', householdGUID, 'accountGroup'],
        queryFn: async () => {
            const [response] = await PortfolioApi().listAccountGroupProfiles(householdGUID!);
            return response ?? undefined;
        },
        select: sortAccountGroupProfiles,
        enabled: !!householdGUID,
    });
};

export const useUpdateAccountGroupAsAdvisor = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: AccountGroupAdvisorUpdateRequest) => {
            return await PortfolioApi().updateAccountGroupAsAdvisor(accountGroupGUID, request);
        },
        onSuccess: () => {
            Toast.success('Account Updated');
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID],
            });
        },
        onError: () => {
            toast.error('Error updating account group');
        },
    });
};

/** Portfolio-level set: enables TLH on every account of the group that supports it (taxable), or disables it on all. */
export const useUpdateAccountGroupTaxLossHarvesting = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: AccountGroupTaxLossHarvestingUpdateRequest) => {
            return await PortfolioApi().updateAccountGroupTaxLossHarvesting(accountGroupGUID, request);
        },
        onSuccess: () => {
            toast.success('Tax loss harvesting updated for all accounts');
            queryClient.invalidateQueries({ queryKey: ['accountGroup', accountGroupGUID] });
            queryClient.invalidateQueries({ queryKey: ['account'] });
        },
        onError: (error) => Toast.forError(error, 'Failed to update tax loss harvesting'),
    });
};

export const useListAccountGroupsByLifecycleStatus = (lifecycleStatuses: LifecycleStatus[]) => {
    return useQuery({
        queryKey: ['accountGroup'],
        queryFn: async () => {
            const [response] = await PortfolioApi().listAccountGroupsByLifecycleStatus({ lifecycleStatuses });
            return response ?? [];
        },
    });
};

export const useListCashflowConfigs = (
    accountGroupGUID: GUID<AccountGroup>,
    cashFlowType?: CashFlowType,
    options?: { throwOnError?: boolean },
) => {
    return useQuery({
        ...options,
        queryKey: ['accountGroup', accountGroupGUID, 'cashFlowList'],
        queryFn: async () => {
            const [response] = await PortfolioApi().listCashFlowConfigs(
                accountGroupGUID,
                cashFlowType && {
                    cashFlowType,
                },
            );
            return response ?? undefined;
        },
        enabled: !!accountGroupGUID,
    });
};

export const useListAccountPlannedWithdrawals = (
    accountGroupGUID: GUID<AccountGroup>,
    accountGUID: GUID<Account>,
    { startMonth, endMonth }: { startMonth: string; endMonth: string },
    options?: { throwOnError?: boolean },
) =>
    useQuery({
        ...options,
        // Share the cashFlowList prefix so cash-flow mutations also refresh assigned withdrawals.
        queryKey: [
            'accountGroup',
            accountGroupGUID,
            'cashFlowList',
            accountGUID,
            'plannedWithdrawals',
            startMonth,
            endMonth,
        ],
        queryFn: async () => {
            const [response] = await PortfolioApi().listAccountPlannedWithdrawals(accountGUID, {
                startMonth,
                endMonth,
            });
            return response ?? [];
        },
        enabled: !!accountGroupGUID && !!accountGUID,
    });

export const useListCashflowConfigsForAccountGroups = (accountGroupGUIDs: GUID<AccountGroup>[]) =>
    useQueries({
        queries: accountGroupGUIDs.map((accountGroupGUID) => ({
            queryKey: ['accountGroup', accountGroupGUID, 'cashFlowList'],
            queryFn: async () => {
                const [response] = await PortfolioApi().listCashFlowConfigs(accountGroupGUID);
                return response ?? [];
            },
        })),
        combine: (results) => ({
            data: results.flatMap(({ data }) => data ?? []),
            isPending: results.some(({ isPending }) => isPending),
            isError: results.some(({ isError }) => isError),
        }),
    });

export const useUpdateCashflowConfig = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            cashFlowConfigGUID,
            cashFlowConfigSaveRequest,
        }: {
            cashFlowConfigGUID: GUID<CashFlowConfig>;
            cashFlowConfigSaveRequest: CashFlowConfigSaveRequest;
        }) => {
            const [response] = await PortfolioApi().updateCashFlowConfig(cashFlowConfigGUID, cashFlowConfigSaveRequest);
            return response ?? undefined;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID, 'cashFlowList'],
            });
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID, 'monthlyCashFlow'],
            });
            toast.success('Cash flow updated successfully.');
        },
        onError: (error) => Toast.forError(error, 'Failed to update cash flow config'),
    });
};

export const useDeleteCashflowConfig = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ cashFlowConfigGUID }: { cashFlowConfigGUID: GUID<CashFlowConfig> }) => {
            const [response] = await PortfolioApi().deleteCashFlowConfig(cashFlowConfigGUID);
            return response ?? undefined;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID, 'cashFlowList'],
            });
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID, 'monthlyCashFlow'],
            });
            toast.success('Cash flow deleted successfully.');
        },
        onError: (error) => Toast.forError(error, 'Failed to delete cash flow config'),
    });
};

export const useGetPortfolioTradePreferences = (
    accountGroupGUID: GUID<AccountGroup>,
    options?: { throwOnError?: boolean },
) => {
    return useQuery({
        ...options,
        queryKey: ['accountGroup', accountGroupGUID, 'tradePreferences'],
        queryFn: async () => {
            const [response] = await PortfolioApi().getPortfolioTradePreferences(accountGroupGUID);
            return response ?? undefined;
        },
        enabled: !!accountGroupGUID,
    });
};

export const useCreateOrUpdateTradeRestrictionAsset = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ tradeRestriction }: { tradeRestriction: TradeRestrictionAssetRequest }) => {
            const [response] = await PortfolioApi().createOrUpdateTradeRestrictionAsset(
                accountGroupGUID,
                tradeRestriction,
            );
            return response ?? undefined;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID, 'tradePreferences'],
            });
            toast.success('Restriction saved successfully.');
        },
        onError: (error) => Toast.forError(error, 'Failed to create or update trade restriction asset'),
    });
};

export const useDeleteTradeRestrictionAsset = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ shareId }: { shareId: string }) => {
            const [response] = await PortfolioApi().deleteTradeRestrictionAsset(accountGroupGUID, { shareId });
            return response ?? undefined;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID, 'tradePreferences'],
            });
            toast.success('Restriction deleted successfully.');
        },
        onError: (error) => Toast.forError(error, 'Failed to remove trade restriction asset'),
    });
};

export const useDefaultAssetLocationPolicy = (options?: { enabled?: boolean; throwOnError?: boolean }) => {
    return useQuery({
        ...options,
        queryKey: ['defaultAssetLocationPolicy'],
        queryFn: async () => {
            const [response] = await PortfolioApi().getDefaultAssetLocationPolicy();
            return response ?? undefined;
        },
    });
};

export const useUpdateAccountGroupAssetLocationPolicy = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (assetLocationPolicy: AssetLocationPolicy) => {
            const [response] = await PortfolioApi().updateAccountGroupAssetLocationPolicy(
                accountGroupGUID,
                assetLocationPolicy,
            );
            return response ?? undefined;
        },
        onSuccess: () => {
            toast.success('Asset location policies updated');
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to update asset location policies'),
    });
};

export const useUpdateAccountConvictions = (accountGroupGuid: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ convictions }: { convictions: AccountConvictionsSaveRequest[] }) => {
            const [response] = await PortfolioApi().updateAccountConvictions(accountGroupGuid, convictions);
            return response ?? undefined;
        },
        onSuccess: () => {
            toast.success('Convictions updated successfully');
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGuid],
            });
            queryClient.invalidateQueries({
                queryKey: ['optimization', 'allocation', accountGroupGuid],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to update convictions'),
    });
};

export const useSetTradeRestrictionCategoriesAndOverrides = (accountGroupGuid: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            tradeRestrictionCategories,
            tradeRestrictionCategoryOverrides,
        }: {
            tradeRestrictionCategories: TradeRestrictionCategoryRequest[];
            tradeRestrictionCategoryOverrides: TradeRestrictionCategoryOverrideRequest[];
        }) => {
            const [categoriesResponse] = await PortfolioApi().setTradeRestrictionCategories(
                accountGroupGuid,
                tradeRestrictionCategories,
            );
            const [overridesResponse] = await PortfolioApi().setTradeRestrictionCategoryOverrides(
                accountGroupGuid,
                tradeRestrictionCategoryOverrides,
            );
            return { categoriesResponse, overridesResponse };
        },
        onSuccess: () => {
            toast.success('Trade restrictions updated successfully');
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGuid],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to update trade restrictions'),
    });
};

export const useUpdateAccountGroupAllocation = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: AccountGroupAllocationRequest) => {
            const [response] = await PortfolioApi().updateAccountGroupAllocation(accountGroupGUID, request);
            return response ?? undefined;
        },
        onSuccess: () => {
            toast.success('Account group allocation updated');
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['optimization', 'allocation', accountGroupGUID],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to update account group allocation'),
    });
};

export const useUpdateAccountAllocations = (accountGroupGUID: GUID<AccountGroup>) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (requests: AccountAllocationRequest[]) => {
            const [response] = await PortfolioApi().updateAccountAllocations(accountGroupGUID, requests);
            return response ?? undefined;
        },
        onSuccess: () => {
            toast.success('Account allocations updated');
            queryClient.invalidateQueries({
                queryKey: ['accountGroup', accountGroupGUID],
            });
            queryClient.invalidateQueries({
                queryKey: ['optimization', 'allocation', accountGroupGUID],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed to update account allocations'),
    });
};
